import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

export const CODE_LENGTH = 4;
export const CODE_COLOURS = ["🔴", "🔵", "🟡", "🟢", "🟣", "🟠"] as const;

export interface CodeGuess {
	seat: PlayerIndex;
	code: number[];
	/** Right colour in the right place. */
	exact: number;
	/** Right colour, wrong place. */
	near: number;
}

export interface CodeBreakerState extends BaseState {
	kind: "codebreaker";
	/** Empty once redacted — nobody sees it until it is cracked. */
	secret: number[];
	guesses: CodeGuess[];
}

export interface CodeBreakerMove {
	code: number[];
}

/** Standard pegs: exact matches first, then colours that are simply present. */
export function scoreGuess(secret: number[], guess: number[]): { exact: number; near: number } {
	const secretLeft: number[] = [];
	const guessLeft: number[] = [];
	let exact = 0;

	for (let i = 0; i < secret.length; i++) {
		if (secret[i] === guess[i]) exact++;
		else {
			secretLeft.push(secret[i]);
			guessLeft.push(guess[i]);
		}
	}

	let near = 0;
	const pool = [...secretLeft];
	for (const colour of guessLeft) {
		const found = pool.indexOf(colour);
		if (found !== -1) {
			near++;
			pool.splice(found, 1);
		}
	}
	return { exact, near };
}

export const codeBreaker: GameDefinition<CodeBreakerState, CodeBreakerMove> = {
	id: "codebreaker",
	category: "puzzle",
	title: "Code Breaker",
	emoji: "🔐",
	blurb: "Four hidden colours. Use the clues and crack it before your friend.",
	howTo: [
		"There's a secret code: four colours, and colours can repeat.",
		"Take turns guessing the whole code.",
		"You're told how many are exactly right, and how many are the right colour in the wrong place.",
		"You both see every guess — first to crack it wins.",
	],

	create(rng) {
		return {
			kind: "codebreaker",
			secret: Array.from({ length: CODE_LENGTH }, () => rng.int(CODE_COLOURS.length)),
			guesses: [],
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const code = (raw as { code?: unknown }).code;
		if (!Array.isArray(code) || code.length !== CODE_LENGTH) return null;
		for (const colour of code) {
			if (typeof colour !== "number" || !Number.isInteger(colour)) return null;
			if (colour < 0 || colour >= CODE_COLOURS.length) return null;
		}
		return { code: code as number[] };
	},

	apply(state, player, move): MoveOutcome<CodeBreakerState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		// Re-guessing tells you nothing you were not already told, and without
		// this the list grows for as long as somebody keeps sending — the whole
		// room is one stored value, so enough of them would push it past the
		// size limit and leave it unable to save anything ever again.
		if (state.guesses.some((guess) => guess.code.join() === move.code.join())) {
			return reject("You've already tried that one.");
		}

		const { exact, near } = scoreGuess(state.secret, move.code);
		const guesses = [...state.guesses, { seat: player, code: move.code, exact, near }];

		if (exact === CODE_LENGTH) {
			const scores: [number, number] = [0, 0];
			scores[player] = 1;
			return {
				ok: true,
				state: { ...state, guesses, scores, turn: null, result: { winner: player } },
			};
		}

		return { ok: true, state: { ...state, guesses, turn: otherPlayer(player) } };
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		// Every code is a legal guess; the whole game is choosing a good one.
		const tried = new Set(state.guesses.map((guess) => guess.code.join(",")));
		const moves: CodeBreakerMove[] = [];
		const total = CODE_COLOURS.length ** CODE_LENGTH;
		for (let index = 0; index < total; index++) {
			const code: number[] = [];
			let rest = index;
			for (let position = 0; position < CODE_LENGTH; position++) {
				code.push(rest % CODE_COLOURS.length);
				rest = Math.floor(rest / CODE_COLOURS.length);
			}
			if (!tried.has(code.join(","))) moves.push({ code });
		}
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},

	redact(state) {
		return state.result ? state : { ...state, secret: [] };
	},
};

/**
 * Every code still consistent with the clues so far — the whole of a good
 * player's reasoning, and what the hard bot guesses from.
 */
export function consistentCodes(guesses: CodeGuess[]): number[][] {
	const codes: number[][] = [];
	const total = CODE_COLOURS.length ** CODE_LENGTH;

	for (let index = 0; index < total; index++) {
		const code: number[] = [];
		let rest = index;
		for (let position = 0; position < CODE_LENGTH; position++) {
			code.push(rest % CODE_COLOURS.length);
			rest = Math.floor(rest / CODE_COLOURS.length);
		}
		const fits = guesses.every((guess) => {
			const { exact, near } = scoreGuess(code, guess.code);
			return exact === guess.exact && near === guess.near;
		});
		if (fits) codes.push(code);
	}
	return codes;
}
